import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { CONTRACTS_API_URL } from '../../constants';
import { formatObligationStatus } from '../../domain/contracts';

interface ObligationEvent {
  date: string;
  kind: string;
  label: string;
}

interface ObligationAlert {
  severity: string;
  message: string;
  date: string;
}

export interface LeaseObligationView {
  primaryTermStart: string;
  primaryTermEnd: string;
  annualRental: number;
  hbp: boolean;
  hbpDate?: string;
  notes?: string;
  status: string;
  events: ObligationEvent[];
  alerts: ObligationAlert[];
}

interface LeaseObligationsPanelProps {
  contractId: string;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  canUpdate: boolean;
}

const empty: LeaseObligationView = {
  primaryTermStart: '',
  primaryTermEnd: '',
  annualRental: 0,
  hbp: false,
  status: '',
  events: [],
  alerts: []
};

export function LeaseObligationsPanel(props: LeaseObligationsPanelProps): ReactElement {
  const { contractId, authFetch, canUpdate } = props;
  const [view, setView] = useState<LeaseObligationView>(empty);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await authFetch(`${CONTRACTS_API_URL}/${contractId}/obligations`);
      if (res.status === 404) {
        setView(empty);
        return;
      }
      if (!res.ok) {
        throw new Error((await res.text()) || `Request failed (${res.status})`);
      }
      setView((await res.json()) as LeaseObligationView);
    } catch (e) {
      setError(e instanceof Error ? e.message.replace(/^"|"$/g, '') : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [authFetch, contractId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onSave(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError('');
    try {
      const res = await authFetch(`${CONTRACTS_API_URL}/${contractId}/obligations`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          primaryTermStart: view.primaryTermStart,
          primaryTermEnd: view.primaryTermEnd,
          annualRental: Number(view.annualRental),
          hbp: view.hbp,
          hbpDate: view.hbp ? view.hbpDate || undefined : undefined,
          notes: view.notes || undefined
        })
      });
      if (!res.ok) {
        throw new Error((await res.text()) || `Request failed (${res.status})`);
      }
      setView((await res.json()) as LeaseObligationView);
    } catch (err) {
      setError(err instanceof Error ? err.message.replace(/^"|"$/g, '') : 'Save failed');
    }
  }

  return (
    <section className="nested-card" aria-label="Obligations" data-testid="lease-obligations">
      <h4 className="form-section-title">Obligations</h4>
      {loading && <p className="hint">Loading…</p>}
      {error && <p className="error">{error}</p>}
      {!loading && view.status ? (
        <dl className="detail-list">
          <dt>Status</dt>
          <dd data-testid="obligation-status">{formatObligationStatus(view.status)}</dd>
        </dl>
      ) : null}
      {view.alerts.length > 0 && (
        <ul className="collaboration-notes-list" data-testid="obligation-alerts">
          {view.alerts.map((a) => (
            <li key={`${a.severity}-${a.date}-${a.message}`} className="collaboration-note-card">
              <div className="collaboration-note-body">
                {a.severity}: {a.message} ({a.date})
              </div>
            </li>
          ))}
        </ul>
      )}
      {view.events.length > 0 && (
        <dl className="detail-list" data-testid="obligation-events">
          {view.events.map((ev) => (
            <FragmentEvent key={`${ev.kind}-${ev.date}`} date={ev.date} label={ev.label} />
          ))}
        </dl>
      )}
      {canUpdate ? (
        <form className="grid" data-testid="obligation-form" onSubmit={(e) => void onSave(e)}>
          <label>
            Primary term start
            <input
              required
              type="date"
              value={view.primaryTermStart}
              onChange={(e) => setView((prev) => ({ ...prev, primaryTermStart: e.target.value }))}
            />
          </label>
          <label>
            Primary term end
            <input
              required
              type="date"
              value={view.primaryTermEnd}
              onChange={(e) => setView((prev) => ({ ...prev, primaryTermEnd: e.target.value }))}
            />
          </label>
          <label>
            Annual rental
            <input
              required
              type="number"
              min="0"
              step="0.01"
              value={view.annualRental}
              onChange={(e) => setView((prev) => ({ ...prev, annualRental: Number(e.target.value) }))}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={view.hbp}
              onChange={(e) => setView((prev) => ({ ...prev, hbp: e.target.checked }))}
            />{' '}
            Held by production
          </label>
          {view.hbp && (
            <label>
              HBP date
              <input
                required
                type="date"
                value={view.hbpDate ?? ''}
                onChange={(e) => setView((prev) => ({ ...prev, hbpDate: e.target.value }))}
              />
            </label>
          )}
          <label>
            Notes
            <input
              value={view.notes ?? ''}
              onChange={(e) => setView((prev) => ({ ...prev, notes: e.target.value }))}
            />
          </label>
          <button type="submit" data-testid="obligation-save">
            Save obligations
          </button>
        </form>
      ) : (
        <p className="hint">Read-only. Lease analysts update obligations.</p>
      )}
    </section>
  );
}

function FragmentEvent({ date, label }: { date: string; label: string }): ReactElement {
  return (
    <>
      <dt>{label}</dt>
      <dd>{date}</dd>
    </>
  );
}
